import type { ProductType, AppProduct } from "@shared/interfaces/product";
import type { SelectedProducts } from "../../types/product";

/**
 * Configuration describing the "Keine Auswahl" placeholder entries available for
 * a goal. `default: true` means the placeholder should win over a concrete product
 * of the same type when pre-selecting defaults.
 */
export type NoneProductConfig = { type: ProductType; default: boolean };

/**
 * Owns the product selection state for the configurator.
 *
 * Extracted from ProductManager so selection logic and the mutable
 * `selectedProducts` map live in one focused, independently testable place.
 * The service is intentionally stateless with respect to the product
 * repository: callers pass the relevant per-goal product list and placeholder
 * configuration into `selectDefaultsForGoal`.
 */
export class ProductSelectionService {
    private selectedProducts: SelectedProducts = {} as SelectedProducts;

    setSelectedProducts(selectedProducts: SelectedProducts): void {
        this.selectedProducts = selectedProducts;
    }

    getSelectedProducts(): SelectedProducts {
        return this.selectedProducts;
    }

    /**
     * Picks one default product per product type for the given goal.
     *
     * A "Keine Auswahl" placeholder (id === 0) is chosen when it is configured as
     * the default for its type; otherwise the first encountered product per type
     * wins. The resulting selection becomes the current selection state.
     */
    selectDefaultsForGoal(
        productsForGoal: AppProduct[],
        noneProducts: NoneProductConfig[],
    ): SelectedProducts {
        const selected = {} as SelectedProducts;

        productsForGoal.forEach(product => {
            if (
                product.id === 0 &&
                noneProducts.find(np => np.type === product.type && np.default)
            ) {
                selected[product.type] = product;
            }
            if (!selected[product.type]) {
                selected[product.type] = product;
            }
        });

        this.selectedProducts = selected;
        return selected;
    }
}
